import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Clock, Play, RotateCcw, Pause, Dice5, Minus, Plus, Hourglass, Timer, Mic, Check, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StudentActionModal from './StudentActionModal';

interface Student {
  id: number;
  name: string;
  currentPoints: number;
  petType: string | null;
  unlockedPets?: string[];
}

export interface TimerState {
  timerMode: 'countdown' | 'stopwatch';
  timeLeft: number;
  initialTime: number;
  isTimerRunning: boolean;
}

interface ToolsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimizeTimer?: () => void;
  students: Student[];
  onUpdate?: (points: number, count: number) => void;
  timerState?: TimerState;
  onTimerStateChange?: (state: TimerState) => void;
}

export default function ToolsModal({ isOpen, onClose, onMinimizeTimer, students, onUpdate, timerState, onTimerStateChange }: ToolsModalProps) {
  const { t } = useTranslation();
  const [activeTool, setActiveTool] = useState<'random' | 'timer' | 'noise'>('timer');

  // Random Picker State
  const [randomResult, setRandomResult] = useState<string[] | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [isRolling, setIsRolling] = useState(false);
  const [pickCount, setPickCount] = useState(1);
  const [excludePicked, setExcludePicked] = useState(() => {
    try { return sessionStorage.getItem('randomPick_exclude') === '1'; } catch { return false; }
  });
  const [pickedIds, setPickedIds] = useState<Set<number>>(() => {
    try {
      const raw = sessionStorage.getItem('randomPick_ids');
      return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
    } catch { return new Set(); }
  });
  const rollingIntervalRef = useRef<number | null>(null);

  // Sync to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('randomPick_exclude', excludePicked ? '1' : '0');
      sessionStorage.setItem('randomPick_ids', JSON.stringify([...pickedIds]));
    } catch {}
  }, [excludePicked, pickedIds]);

  // Student Action Modal State
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);


  // Timer State — lifted to parent when timerState prop is provided
  const [_timerMode, _setTimerMode] = useState<'countdown' | 'stopwatch'>('countdown');
  const [_timeLeft, _setTimeLeft] = useState(0);
  const [_initialTime, _setInitialTime] = useState(0);
  const [_isTimerRunning, _setIsTimerRunning] = useState(false);
  const [customMinutes, setCustomMinutes] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const timerIntervalRef = useRef<number | null>(null);

  // Use external state if provided, otherwise use internal state
  const timerMode = timerState?.timerMode ?? _timerMode;
  const timeLeft = timerState?.timeLeft ?? _timeLeft;
  const initialTime = timerState?.initialTime ?? _initialTime;
  const isTimerRunning = timerState?.isTimerRunning ?? _isTimerRunning;

  const setTimerMode = (v: 'countdown' | 'stopwatch') => {
    _setTimerMode(v);
  };
  const setTimeLeft = (v: number | ((prev: number) => number)) => {
    _setTimeLeft(v);
  };
  const setInitialTime = (v: number) => {
    _setInitialTime(v);
  };
  const setIsTimerRunning = (v: boolean) => {
    _setIsTimerRunning(v);
  };

  // Sync internal timer state to parent via effect (avoids setState-during-render warning)
  useEffect(() => {
    if (onTimerStateChange) {
      onTimerStateChange({ timerMode: _timerMode, timeLeft: _timeLeft, initialTime: _initialTime, isTimerRunning: _isTimerRunning });
    }
  }, [_timerMode, _timeLeft, _initialTime, _isTimerRunning]);

  // Sync parent state back to internal state when modal opens (e.g. after minimized ticking)
  useEffect(() => {
    if (isOpen && timerState) {
      _setTimerMode(timerState.timerMode);
      _setTimeLeft(timerState.timeLeft);
      _setInitialTime(timerState.initialTime);
      _setIsTimerRunning(timerState.isTimerRunning);
    }
  }, [isOpen]);

  // Noise Detector State
  const [noiseLevel, setNoiseLevel] = useState(0);
  const [sensitivity, setSensitivity] = useState(50);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const sensitivityRef = useRef(50);

  // Sync sensitivity ref for use in animation frame
  useEffect(() => {
    sensitivityRef.current = sensitivity;
  }, [sensitivity]);

  useEffect(() => {
    if (!isOpen) {
      // Reset states when closed
      setRandomResult(null);
      setSelectedStudents([]);
      setIsRolling(false);
      setPickCount(1);
      // excludePicked and pickedIds are persisted in sessionStorage, don't reset on close
      if (rollingIntervalRef.current) clearInterval(rollingIntervalRef.current);

      setIsActionModalOpen(false);

      // Only reset timer if it's not running (preserve state when minimized)
      if (!isTimerRunning) {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setTimeLeft(0);
        setInitialTime(0);
        setTimerMode('countdown');
        setShowCustomInput(false);
        setCustomMinutes('');
      } else {
        setShowCustomInput(false);
        setCustomMinutes('');
      }

      stopNoiseDetection();
    }
  }, [isOpen]);

  // Fisher-Yates shuffle for unbiased randomness
  const fisherYatesShuffle = <T,>(arr: T[]): T[] => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Available students (excluding already picked ones if option is on)
  const availableStudents = excludePicked
    ? students.filter(s => !pickedIds.has(s.id))
    : students;

  // Random Picker Logic
  const startRandomPick = () => {
    if (availableStudents.length === 0) return;

    setIsRolling(true);
    let count = 0;
    const maxCount = 20;
    const interval = 80;

    if (rollingIntervalRef.current) clearInterval(rollingIntervalRef.current);

    rollingIntervalRef.current = window.setInterval(() => {
      const randomIndex = Math.floor(Math.random() * availableStudents.length);
      setRandomResult([availableStudents[randomIndex].name]);
      count++;

      if (count >= maxCount) {
        if (rollingIntervalRef.current) clearInterval(rollingIntervalRef.current);
        setIsRolling(false);

        const shuffled = fisherYatesShuffle(availableStudents);
        const selected = shuffled.slice(0, Math.min(pickCount, availableStudents.length));
        setRandomResult(selected.map(s => s.name));
        setSelectedStudents(selected);

        if (excludePicked) {
          setPickedIds(prev => {
            const next = new Set(prev);
            selected.forEach(s => next.add(s.id));
            return next;
          });
        }
      }
    }, interval);
  };

  const adjustPickCount = (delta: number) => {
    setPickCount(prev => Math.max(1, Math.min(students.length, prev + delta)));
  };

  // Sound Effect
  const playTickSound = () => {
    try {
      const AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      
      // Create oscillator
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1); // Pitch drop effect
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {
      console.error('Audio play failed', e);
    }
  };

  const playEndSound = () => {
    try {
      const AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      const startTimes = [0, 0.1, 0.2, 0.3];
      
      notes.forEach((note, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'triangle';
        osc.frequency.value = note;
        
        const startTime = ctx.currentTime + startTimes[i];
        const duration = 0.6;
        
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.1, startTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(startTime);
        osc.stop(startTime + duration);
      });
    } catch (e) {
      console.error('Audio play failed', e);
    }
  };

  // Timer Logic
  const setCountdown = (seconds: number) => {
    setTimeLeft(seconds);
    setInitialTime(seconds);
    setIsTimerRunning(false);
    setShowCustomInput(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
  };

  const adjustTime = (seconds: number) => {
    if (timerMode === 'countdown' && !isTimerRunning) {
      const newTime = Math.max(0, timeLeft + seconds);
      setTimeLeft(newTime);
      setInitialTime(newTime);
    }
  };

  const handleCustomSubmit = () => {
    // Check if input contains colon for MM:SS format or just number for minutes
    let totalSeconds = 0;
    
    if (customMinutes.includes(':') || customMinutes.includes('：')) {
        // MM:SS format
        const parts = customMinutes.replace('：', ':').split(':');
        const mins = parseInt(parts[0]) || 0;
        const secs = parseInt(parts[1]) || 0;
        totalSeconds = mins * 60 + secs;
    } else {
        // Minutes format (support decimals like 0.5)
        const mins = parseFloat(customMinutes);
        if (!isNaN(mins)) {
            totalSeconds = Math.round(mins * 60);
        }
    }

    if (totalSeconds > 0) {
      setCountdown(totalSeconds);
    }
  };

  const switchTimerMode = (mode: 'countdown' | 'stopwatch') => {
    setTimerMode(mode);
    setIsTimerRunning(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setTimeLeft(0);
    setInitialTime(0);
    setShowCustomInput(false);
  };

  const toggleTimer = () => {
    // Prevent starting if countdown is at 0 and no initial time set
    if (timerMode === 'countdown' && timeLeft <= 0 && !isTimerRunning && initialTime === 0) return;

    if (isTimerRunning) {
      setIsTimerRunning(false);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    } else {
      // Auto-reset if countdown finished
      if (timerMode === 'countdown' && timeLeft === 0 && initialTime > 0) {
        setTimeLeft(initialTime);
      }
      
      setIsTimerRunning(true);
      timerIntervalRef.current = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (timerMode === 'countdown') {
            // Handle the first tick after auto-reset
            // If prev is 0 (which happens if setTimeLeft(initialTime) hasn't processed yet),
            // use initialTime as the base
            const currentTime = prev === 0 && initialTime > 0 ? initialTime : prev;
            const next = currentTime - 1;
            
            // Play sound for last 10 seconds
            if (next > 0 && next <= 10) {
               playTickSound(); 
            } else if (next === 0) {
               playEndSound(); // End sound
            }

            if (next <= 0) {
              if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
              setIsTimerRunning(false);
              return 0;
            }
            return next;
          } else {
            // Stopwatch: increment
            return prev + 1;
          }
        });
      }, 1000);
    }
  };

  const resetTimer = () => {
    setIsTimerRunning(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (timerMode === 'countdown') {
      setTimeLeft(initialTime);
    } else {
      setTimeLeft(0);
    }
  };

  // Noise Detector Logic
  const stopNoiseDetection = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }
  };

  const startNoiseDetection = async () => {
    try {
      stopNoiseDetection(); // Ensure clean start
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContext) {
        alert('当前设备不支持音频处理');
        return;
      }
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      microphone.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      microphoneRef.current = microphone;
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const updateNoiseLevel = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        
        // Normalize based on sensitivity
        const sensitivityVal = sensitivityRef.current;
        const multiplier = 0.5 + (sensitivityVal / 100) * 4.5; // Range 0.5 to 5.0
        
        const rawLevel = (average / 128) * 100 * multiplier;
        const normalizedLevel = Math.min(100, Math.max(0, rawLevel));
        
        setNoiseLevel(prev => {
            // Smooth transition
            return prev + (normalizedLevel - prev) * 0.2;
        });
        
        rafIdRef.current = requestAnimationFrame(updateNoiseLevel);
      };
      
      updateNoiseLevel();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('无法访问麦克风，请检查权限设置');
    }
  };

  useEffect(() => {
    if (activeTool === 'noise' && isOpen) {
      startNoiseDetection();
    } else {
      stopNoiseDetection();
    }
    return () => {
      stopNoiseDetection();
    };
  }, [activeTool, isOpen]);

  const formatTimeParts = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return {
      m: m.toString().padStart(2, '0'),
      s: s.toString().padStart(2, '0')
    };
  };

  if (!isOpen) return null;

  const timeParts = formatTimeParts(timeLeft);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-clay-lg shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[80vh]">
        {/* Header — 改为粉橘渐变与品牌一致 */}
        <div className="bg-gradient-to-r from-[#ff4da6] via-[#ff7a59] to-[#ffd166] p-4 sm:p-6 text-white relative shrink-0">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 z-10">
            {activeTool === 'timer' && isTimerRunning && onMinimizeTimer && (
              <button
                onClick={onMinimizeTimer}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-sm"
                title={t('home.tools.timer.minimize', '最小化')}
              >
                <Minimize2 className="w-5 h-5 text-white" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-sm"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Dice5 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-black tracking-wide">{t('home.tools.title')}</h2>
          </div>

          <div className="flex p-1 bg-white/25 rounded-2xl backdrop-blur-md relative z-10">
            <button
              onClick={() => setActiveTool('timer')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                activeTool === 'timer' ? 'bg-white text-pink-600 shadow-md shadow-pink-300/30' : 'text-white hover:bg-white/10'
              }`}
            >
              <Clock className="w-4 h-4 hidden sm:block" /> {t('home.tools.timer.title')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-8 overflow-y-auto custom-scrollbar flex-1 bg-gray-50/50">
          {activeTool === 'random' && (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
              
              <div className="flex flex-col items-center gap-3 mb-8">
                <div className="flex items-center gap-4 bg-white rounded-2xl px-6 py-3 shadow-sm border border-gray-100">
                  <span className="text-gray-500 font-bold text-sm">{t('home.tools.random.count')}</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => adjustPickCount(-1)}
                      disabled={isRolling || pickCount <= 1}
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-black text-xl text-blue-600 w-8 text-center">{pickCount}</span>
                    <button
                      onClick={() => adjustPickCount(1)}
                      disabled={isRolling || pickCount >= availableStudents.length}
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={excludePicked}
                      onChange={(e) => {
                        setExcludePicked(e.target.checked);
                        if (!e.target.checked) setPickedIds(new Set());
                      }}
                      disabled={isRolling}
                      className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-500 font-bold">{t('home.tools.random.excludePicked', '不重复抽取')}</span>
                  </label>
                  {excludePicked && pickedIds.size > 0 && (
                    <>
                      <span className="text-xs text-gray-400">
                        ({t('home.tools.random.remaining', '剩余 {{count}} 人', { count: availableStudents.length })})
                      </span>
                      <button
                        onClick={() => setPickedIds(new Set())}
                        disabled={isRolling}
                        className="text-xs text-blue-500 hover:text-blue-600 font-bold disabled:opacity-50"
                      >
                        {t('home.tools.random.resetPicked', '重置')}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {randomResult && randomResult.length > 1 && !isRolling ? (
                 <div className="w-full max-w-md grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8 animate-in zoom-in duration-300 max-h-[300px] overflow-y-auto custom-scrollbar p-1">
                    {randomResult.map((name, idx) => (
                        <div key={idx} className="bg-white border-2 border-blue-100 rounded-xl p-3 text-center shadow-sm flex items-center justify-center min-h-[60px]">
                            <span className="font-bold text-gray-800 truncate block text-lg">{name}</span>
                        </div>
                    ))}
                 </div>
              ) : (
                <div className={`
                  w-48 h-48 rounded-full flex items-center justify-center mb-8 border-8 shadow-xl transition-all duration-300
                  ${randomResult 
                    ? 'bg-white border-blue-500 text-blue-600 scale-110' 
                    : 'bg-gray-100 border-gray-200 text-gray-400'
                  }
                `}>
                  {randomResult ? (
                      <span className="text-3xl font-black text-center px-4 animate-in zoom-in duration-300 break-all leading-tight">
                          {randomResult[0]}
                      </span>
                  ) : (
                      <span className="text-6xl opacity-20">?</span>
                  )}
                </div>
              )}

              <div className="flex gap-4 w-full max-w-xs">
                {randomResult && !isRolling ? (
                    <>
                     <button
                        onClick={startRandomPick}
                        className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-bold text-lg shadow-lg transition-all transform hover:-translate-y-1 active:translate-y-0"
                     >
                        {t('home.tools.random.action')}
                     </button>
                     <button
                        onClick={() => setIsActionModalOpen(true)}
                        className="flex-1 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-bold text-lg shadow-lg shadow-blue-200 transition-all transform hover:-translate-y-1 active:translate-y-0 flex items-center justify-center gap-2"
                     >
                        {t('home.tools.random.review')}
                     </button>
                    </>
                ) : (
                    <button
                        onClick={startRandomPick}
                        disabled={isRolling || availableStudents.length === 0}
                        className={`
                        flex-1 py-4 rounded-2xl font-bold text-lg shadow-lg transition-all transform flex items-center justify-center gap-2
                        ${isRolling 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-200 hover:-translate-y-1 active:translate-y-0'
                        }
                        `}
                    >
                        {isRolling ? t('home.tools.random.rolling', '抽取中...') : t('home.tools.random.action')}
                    </button>
                )}
              </div>
              {students.length === 0 && (
                  <p className="mt-4 text-gray-400 text-sm">{t('home.empty.noStudents.title')}</p>
              )}
            </div>
          )}
          {activeTool === 'timer' && (
            <div className="flex flex-col items-center justify-center h-full min-h-[260px]">
              {/* Timer Mode Switch — iOS 分段控件 */}
              <div className="flex items-center gap-1 mb-6 sm:mb-8 bg-pink-50/70 p-1 rounded-2xl">
                <button
                  onClick={() => switchTimerMode('countdown')}
                  className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                    timerMode === 'countdown'
                      ? 'bg-white text-pink-600 shadow-sm shadow-pink-100/60'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Hourglass className="w-4 h-4" /> {t('home.tools.timer.countdown')}
                </button>
                <button
                  onClick={() => switchTimerMode('stopwatch')}
                  className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                    timerMode === 'stopwatch'
                      ? 'bg-white text-pink-600 shadow-sm shadow-pink-100/60'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Timer className="w-4 h-4" /> {t('home.tools.timer.stopwatch')}
                </button>
              </div>

              {/* Timer Display with Adjust Controls */}
              <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 sm:mb-10">
                {/* Minutes */}
                <div className="flex flex-col items-center gap-1 sm:gap-2">
                  {timerMode === 'countdown' && !isTimerRunning && (
                    <button
                      onClick={() => adjustTime(60)}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  )}
                  <div className={`
                    text-6xl sm:text-8xl font-black font-mono tracking-tighter transition-colors w-[4.5rem] sm:w-40 text-center
                    ${timerMode === 'countdown' && timeLeft <= 10 && timeLeft > 0 && isTimerRunning ? 'text-red-500 animate-pulse' : 'text-gray-800'}
                    ${timerMode === 'countdown' && timeLeft === 0 && initialTime > 0 ? 'text-gray-300' : ''}
                  `}>
                    {timeParts.m}
                  </div>
                  {timerMode === 'countdown' && !isTimerRunning && (
                    <button
                      onClick={() => adjustTime(-60)}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Minus className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  )}
                </div>

                {/* Separator */}
                <div className={`
                  text-6xl sm:text-8xl font-black font-mono sm:pb-4
                  ${timerMode === 'countdown' && timeLeft <= 10 && timeLeft > 0 && isTimerRunning ? 'text-red-500 animate-pulse' : 'text-gray-800'}
                  ${timerMode === 'countdown' && timeLeft === 0 && initialTime > 0 ? 'text-gray-300' : ''}
                `}>:</div>

                {/* Seconds */}
                <div className="flex flex-col items-center gap-1 sm:gap-2">
                  {timerMode === 'countdown' && !isTimerRunning && (
                    <button
                      onClick={() => adjustTime(5)}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  )}
                  <div className={`
                    text-6xl sm:text-8xl font-black font-mono tracking-tighter transition-colors w-[4.5rem] sm:w-40 text-center
                    ${timerMode === 'countdown' && timeLeft <= 10 && timeLeft > 0 && isTimerRunning ? 'text-red-500 animate-pulse' : 'text-gray-800'}
                    ${timerMode === 'countdown' && timeLeft === 0 && initialTime > 0 ? 'text-gray-300' : ''}
                  `}>
                    {timeParts.s}
                  </div>
                  {timerMode === 'countdown' && !isTimerRunning && (
                    <button
                      onClick={() => adjustTime(-5)}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Minus className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  )}
                </div>
              </div>

              {/* Preset Buttons (Only for Countdown) */}
              {timerMode === 'countdown' && !isTimerRunning && (
                <div className="flex flex-col items-center gap-3 mb-6 sm:mb-10 w-full">
                  {!showCustomInput ? (
                    <>
                      <div className="flex gap-2 sm:gap-3 flex-wrap justify-center">
                        {[1, 3, 5, 10, 15].map(min => (
                            <button
                                key={min}
                                onClick={() => setCountdown(min * 60)}
                                className={`
                                    px-3 sm:px-4 py-2 rounded-xl font-bold text-sm transition-all border-2
                                    ${initialTime === min * 60
                                        ? 'bg-blue-50 border-blue-200 text-blue-600'
                                        : 'bg-white border-transparent hover:border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }
                                `}
                            >
                                {min} {t('home.tools.timer.minutes')}
                            </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setShowCustomInput(true)}
                        className="text-blue-500 text-sm font-bold hover:underline"
                      >
                        {t('home.tools.timer.custom')}
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                      <input
                        type="text"
                        value={customMinutes}
                        onChange={(e) => setCustomMinutes(e.target.value)}
                        placeholder={t('home.tools.timer.placeholder')}
                        className="w-28 sm:w-32 px-3 sm:px-4 py-2 rounded-xl border-2 border-blue-200 focus:border-blue-500 outline-none text-center font-bold"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                      />
                      <button
                        onClick={handleCustomSubmit}
                        className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors"
                      >
                        {t('home.tools.timer.set')}
                      </button>
                      <button
                        onClick={() => setShowCustomInput(false)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Spacer for Stopwatch mode to keep layout consistent */}
              {timerMode === 'stopwatch' && <div className="h-12 sm:h-20 mb-6 sm:mb-10"></div>}
              {timerMode === 'countdown' && isTimerRunning && <div className="h-12 sm:h-20 mb-6 sm:mb-10"></div>}

              {/* Controls */}
              <div className="flex gap-3 sm:gap-4 w-full max-w-xs">
                <button
                    onClick={toggleTimer}
                    disabled={timerMode === 'countdown' && timeLeft === 0 && initialTime === 0}
                    className={`
                        flex-1 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg shadow-lg transition-all transform flex items-center justify-center gap-2
                        ${isTimerRunning
                            ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                            : 'bg-blue-500 text-white hover:bg-blue-600 shadow-blue-200 hover:-translate-y-1'
                        }
                        ${timerMode === 'countdown' && timeLeft === 0 && initialTime === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    {isTimerRunning ? (
                        <><Pause className="w-5 h-5 fill-current" /> {t('home.tools.timer.pause')}</>
                    ) : (
                        <><Play className="w-5 h-5 fill-current" /> {t('home.tools.timer.start')}</>
                    )}
                </button>

                <button
                    onClick={resetTimer}
                    disabled={timeLeft === 0 && initialTime === 0}
                    className="w-14 sm:w-16 rounded-2xl font-bold text-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-all flex items-center justify-center"
                >
                    <RotateCcw className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          {activeTool === 'noise' && (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
              {/* Gauge Visualization */}
              <div className="relative w-64 h-64 mb-8">
                 {/* Background Circle */}
                 <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#f3f4f6"
                      strokeWidth="10"
                    />
                    {/* Value Circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset={`${2 * Math.PI * 45 * (1 - noiseLevel / 100)}`}
                      className={`transition-all duration-200 ${
                        noiseLevel > 80 ? 'text-red-500' : 
                        noiseLevel > 50 ? 'text-yellow-500' : 'text-green-500'
                      }`}
                      strokeLinecap="round"
                    />
                 </svg>
                 
                 {/* Center Display */}
                 <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-black transition-colors ${
                        noiseLevel > 80 ? 'text-red-500' : 
                        noiseLevel > 50 ? 'text-yellow-500' : 'text-green-500'
                    }`}>
                        {Math.round(noiseLevel)}
                    </span>
                    <span className="text-gray-400 text-sm font-bold mt-1">{t('home.tools.noise.level', '当前噪音')}</span>
                 </div>
              </div>

              {/* Sensitivity Control */}
              <div className="w-full max-w-xs bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-gray-500">{t('home.tools.noise.sensitivity', '灵敏度')}</span>
                    <span className="text-sm font-bold text-blue-600">{sensitivity}%</span>
                </div>
                <input
                    type="range"
                    min="1"
                    max="100"
                    value={sensitivity}
                    onChange={(e) => setSensitivity(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
              </div>
              
              <p className="mt-6 text-gray-400 text-sm text-center max-w-xs">
                {t('home.tools.noise.hint', '请允许使用麦克风以检测噪音')}
              </p>
            </div>
          )}
        </div>
      </div>
      
      <StudentActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        students={selectedStudents}
        onUpdate={(points: number) => {
            if (onUpdate) onUpdate(points, selectedStudents.length);
            setIsActionModalOpen(false);
        }}
      />
    </div>
  );
}
